import { Moon, Sun } from 'lucide-react'

import { cn } from '@/shared/lib/cn'
import { useUiStore } from '@/shared/stores/ui-store'
import { useT } from '@/shared/i18n'

export interface ThemeToggleProps {
  className?: string
  withLabel?: boolean
}

/**
 * Oscuro por defecto, claro real. El valor se persiste en localStorage y el
 * script inline de `index.html` lo aplica antes del primer paint: sin eso hay
 * flash blanco en cada recarga.
 */
export function ThemeToggle({ className, withLabel = false }: ThemeToggleProps) {
  const t = useT()
  const theme = useUiStore((state) => state.theme)
  const toggleTheme = useUiStore((state) => state.toggleTheme)

  // El nombre del modo al que se VA, como pieza de la frase. Va con marcador
  // con nombre y no concatenado: en inglés el orden es "Switch to dark mode".
  const next = t(theme === 'dark' ? 'appearance.theme.nameLight' : 'appearance.theme.nameDark')
  const switchLabel = t('appearance.theme.switchTo', { modo: next })

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={switchLabel}
      title={switchLabel}
      data-testid="theme-toggle"
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-2',
        'text-text-2 transition-colors duration-[var(--motion-fast)]',
        'hover:bg-surface-2 hover:text-text-1',
        className,
      )}
    >
      {theme === 'dark' ? (
        <Moon size={14} aria-hidden="true" />
      ) : (
        <Sun size={14} aria-hidden="true" />
      )}
      {withLabel ? (
        <span className="text-xs">
          {t(theme === 'dark' ? 'appearance.theme.dark' : 'appearance.theme.light')}
        </span>
      ) : null}
    </button>
  )
}
